import { useState } from "react";
import api, { apiErr } from "@/lib/api";
import { DatesDialog } from "@/components/DatesDialog";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ChevronDown, CalendarClock, Clock, AlertTriangle, Info } from "lucide-react";
import { DeedDocs } from "@/components/dossier/DeedBlockers";
import { scrollToSec } from "@/components/DeedChecks";
import { openSpecial } from "@/components/SpecialSituations";
import { QuestionsDialog } from "@/components/QuestionsDialog";
import { MismatchReview } from "@/components/MismatchReview";
import { Glossed } from "@/components/Glossary";
import { remindAction } from "@/components/RemindPreview";
import { WhatsAppIcon } from "@/components/WhatsAppIcon";
import { openContacts } from "@/components/ProcessSidebar";

// O texto não repete o que a etiqueta já diz (ex.: «sem resposta a 2 lembretes»).
const dupOfTag = (ln) => !!ln.tag && !!ln.text && ln.tag.toLowerCase().includes(ln.text.toLowerCase());

const btn = "inline-flex items-center justify-center gap-1 text-[15px] md:text-sm font-semibold text-center leading-tight md:whitespace-nowrap px-3 md:px-4 py-2 md:py-0 min-h-[48px] md:min-h-0 md:h-10 rounded-[14px] border border-slate-300 bg-white text-ec-green hover:bg-slate-50 transition-colors flex-1 md:flex-none md:min-w-[96px]";
const btnDark = "inline-flex items-center justify-center gap-1 text-[15px] md:text-sm font-semibold text-center leading-tight md:whitespace-nowrap px-3 md:px-4 py-2 md:py-0 min-h-[48px] md:min-h-0 md:h-10 rounded-[14px] bg-ec-green text-white hover:bg-ec-greenDark transition-colors flex-1 md:flex-none md:min-w-[96px]";
const BAR = { red: "bg-red-600", amber: "bg-amber-500" };
const DARK = new Set(["review", "dates", "pep", "remind", "request", "contact", "call"]);

// Linhas do «A tratar» vêm prontas do servidor (business.a_tratar): ordem, gravidade, texto e botão.
function LineButton({ ln, run, fixed = false }) {
  const b = ln.button;
  if (!b) return null;
  const testid = b.kind === "review" ? `pending-review-${ln.id}` : b.kind === "dates" ? "pending-review-dates" : b.kind === "pep" ? "pending-pep-approve" : `pending-action-${ln.id}`;
  const main = <button className={DARK.has(b.kind) && !b.link ? btnDark : btn} onClick={() => run(ln)} data-testid={testid} data-remind={b.kind === "remind" ? b.role : undefined}>{b.kind === "remind" && <span className="grid place-items-center w-6 h-6 rounded-full bg-white"><WhatsAppIcon size={15} /></span>}{b.label}</button>;
  if (!b.link) return <div className={`flex w-full md:w-auto shrink-0 ${fixed ? "lg:min-w-[200px] lg:[&>button]:flex-1" : ""}`}>{main}</div>;
  return <div className="flex w-full md:w-auto gap-2 shrink-0"><a href={b.link.url} target="_blank" rel="noopener noreferrer" className={btnDark} data-testid={`pending-link-${ln.id}`}>{b.link.label}</a>{main}</div>;
}

function Tag({ ln }) {
  if (!ln.tag) return null;
  return <span className="inline-block mb-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 text-xs font-bold uppercase tracking-wide" data-testid={`pending-tag-${ln.id}`}>{ln.tag}</span>;
}

function StatusNote({ status, boxed = false }) {
  if (!status) return null;
  const t = <span data-testid="next-step-status"><Clock size={13} className="inline -mt-0.5 mr-1" />{status.label}</span>;
  if (!boxed) return <div className="mt-2 text-sm text-slate-500">{t}</div>;
  return <div className="rounded-[14px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600" data-testid="process-status-note">{t}</div>;
}

function NextCard({ ln, run, mineLabel = false }) {
  return (
    <div className="rounded-[18px] border-2 border-slate-900 bg-white p-[18px] md:p-6 md:flex md:items-center md:gap-5 shadow-[0_8px_30px_rgb(0,0,0,0.06)]" data-testid="next-step-card">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 text-xs font-bold tracking-wider text-slate-500 uppercase"><CalendarClock size={16} className="text-ec-green" />{mineLabel ? "Próxima ação · tua" : "Próxima ação"}</div>
        <Tag ln={ln} />
        <div className="text-[19px] md:text-xl font-semibold text-slate-900 mt-1 leading-snug" data-testid={`pending-title-${ln.id}`}><Glossed text={ln.title} /></div>
        {ln.text && !dupOfTag(ln) && <div className="text-[15px] text-slate-600 mt-0.5" data-testid={`pending-text-${ln.id}`}><Glossed text={ln.text} /></div>}
      </div>
      <div className="mt-4 md:mt-0 md:text-right" data-testid={`pending-${ln.id}`}><LineButton ln={ln} run={run} /></div>
    </div>
  );
}

function Line({ ln, run }) {
  const [more, setMore] = useState(false);
  return (
    <div className="relative flex flex-wrap md:flex-nowrap items-center gap-x-4 gap-y-3 pl-[18px] pr-4 md:pr-5 py-4" data-testid={`pending-${ln.id}`}>
      <span className={`absolute left-0 top-3 bottom-3 w-1 rounded-r ${BAR[ln.tone]}`} data-testid={`pending-tone-${ln.id}-${ln.tone}`} />
      <div className="flex-1 min-w-0 basis-full md:basis-auto">
        <Tag ln={ln} />
        <div className="text-[15px] font-semibold text-ec-green" data-testid={`pending-title-${ln.id}`}><Glossed text={ln.title} /></div>
        {ln.text && !dupOfTag(ln) && <div className="text-sm text-slate-500" data-testid={`pending-text-${ln.id}`}><Glossed text={ln.text} /></div>}
        {ln.more?.length > 0 && (
          <button onClick={() => setMore(!more)} className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-ec-green" data-testid={`pending-more-info-${ln.id}`}>
            Saber mais <ChevronDown size={12} className={`transition-transform ${more ? "rotate-180" : ""}`} />
          </button>
        )}
        {more && <ul className="mt-1 text-sm text-slate-600 list-disc pl-5 space-y-0.5" data-testid={`pending-more-list-${ln.id}`}>{ln.more.map((m, i) => <li key={i}>{m}</li>)}</ul>}
      </div>
      {ln.owner_note && <span className="text-sm text-slate-500 shrink-0" data-testid={`pending-owner-${ln.id}`}>{ln.owner_note}</span>}
      <LineButton ln={ln} run={run} fixed />
    </div>
  );
}

function HandoffCard({ h, lines = [] }) {
  const dup = lines.some((ln) => ln.title && h.text?.includes(ln.title));
  return (
    <div className="rounded-[14px] border border-slate-200 bg-slate-50 px-[18px] py-3 md:px-5 flex items-start gap-2.5" data-testid="handoff-card">
      <Info size={16} className="text-slate-500 shrink-0 mt-0.5" />
      <div className="min-w-0">
        <div className="text-[15px] font-semibold text-slate-800" data-testid="handoff-title">{h.title}</div>
        {!dup && <div className="text-sm text-slate-500 mt-0.5" data-testid="handoff-text">{h.text}</div>}
      </div>
    </div>
  );
}

function OwnerGroups({ at, first, run }) {
  return (
    <div className="bg-white rounded-[18px] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 overflow-hidden" data-testid="pending-groups">
      {at.groups.map((g) => (
        <div key={g.key} data-testid={`pending-group-${g.key}`}>
          <div className="px-[18px] md:px-6 pt-4 pb-2 flex items-center gap-2 text-xs font-bold tracking-wider text-slate-500 uppercase">
            <span className="grid place-items-center w-6 h-6 rounded-full bg-slate-100 text-slate-600 text-[11px] normal-case">{g.key === "cliente" ? "C" : g.label === "Contigo" ? "Tu" : g.label.split(" ").pop()[0]}</span>
            <span data-testid={`pending-group-title-${g.key}`}>{g.label} · {g.n}</span>
          </div>
          <div className="divide-y divide-slate-100 border-t border-slate-100">
            {at.lines.filter((ln) => ln.dono === g.key && ln !== first).map((ln) => <Line key={ln.id} ln={ln} run={run} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

export function PendingBlock({ pid, p, links, onFocus, onChange, head = null, between = null, flat = false, status = null }) {
  const { user } = useAuth();
  const at = p.a_tratar || { lines: [], visible: 3, more: 0 };
  const info = at.handoff ? <HandoffCard h={at.handoff} lines={at.lines} /> : null;
  if (!head && status && !flat && !at.lines.length) head = <StatusNote status={status} boxed />;
  const [all, setAll] = useState(false);
  const [reviewing, setReviewing] = useState(null);
  const [dates, setDates] = useState(false);
  const [asking, setAsking] = useState(null);
  const run = async (ln) => {
    const b = ln.button;
    if (b.kind === "focus") onFocus(b.doc_key);
    else if (b.kind === "call") { if (b.phone) window.location.href = `tel:${b.phone}`; else openContacts("[data-testid=party-vendedor]"); }
    else if (b.kind === "remind") {
      const lk = (links || []).find((l) => l.role === b.role && (l.party_index || 0) === 0);
      if (!lk) { onFocus(b.doc_key); return; }
      const d = String(lk.phone || "").replace(/\D/g, "");
      const sent = await remindAction({ pid, token: lk.token, phone: d.length === 9 ? `351${d}` : d, button: b.label, reminder_keys: b.reminder_keys, reminder_docs: b.reminder_docs, reminder_doc: b.reminder_docs?.length === 1 ? b.reminder_docs[0] : null });
      if (sent) { const who = b.label.replace(/^(Lembrar|Pedir (à|ao)) /, ""); toast.success(`Enviado ${who.startsWith("Sra.") ? "à" : "ao"} ${who}`, { duration: 10000 }); }
      onChange();
    }
    else if (b.kind === "contact") openContacts(`[data-testid=party-${b.role}]`);
    else if (b.kind === "section") { if (b.sec === "partes") openContacts("[data-testid=party-comprador]"); else scrollToSec(b.sec); }
    else if (b.kind === "review") setReviewing(b.review);
    else if (b.kind === "dates") setDates(true);
    else if (b.kind === "questions") setAsking(b);
    else if (b.kind === "request") {
      const before = Object.fromEntries((p.documents || []).filter((x) => b.doc_keys.includes(x.key)).map((x) => [x.key, x.status]));
      try {
        await Promise.all(b.doc_keys.map((k) => api.put(`/processes/${pid}/documents/${k}`, { status: "pedido" })));
        toast.success("Marcado como pedido", { duration: 10000, action: { label: "Desfazer", onClick: async () => {
          await Promise.all(b.doc_keys.map((k) => api.put(`/processes/${pid}/documents/${k}`, { status: before[k] || "em_falta" })));
          onChange();
        } } });
        onChange();
      }
      catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    }
    else if (b.kind === "special") openSpecial(b.special);
    else if (b.kind === "pep" && user?.role === "direccao") {
      try { await api.post(`/processes/${pid}/pep-approve`); toast.success("PEP aprovado pela direção"); onChange(); }
      catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
    }
  };
  if (!at.lines.length && !head && !flat) return <>{info}{between}</>;
  const dialogs = (
    <>
      {reviewing && <MismatchReview p={p} item={reviewing} onClose={() => setReviewing(null)} onDone={onChange} />}
      {asking && <QuestionsDialog p={p} keys={asking.keys} who={asking.who} onClose={() => setAsking(null)} onSaved={onChange} />}
      <DatesDialog p={p} open={dates} onClose={() => setDates(false)} onSaved={() => { setDates(false); onChange(); }} />
    </>
  );
  // Sem cartão grande: a ação principal está no topo; aqui fica o que trava, cada linha com a sua ação.
  if (flat && !at.handoff) {
    const covered = new Set(at.lines.flatMap((ln) => [ln.button?.doc_key, ...(ln.button?.doc_keys || []), ...(ln.button?.reminder_keys || []), ...(ln.remind?.reminder_keys || [])]).filter(Boolean));
    const deedDocs = (p.todo?.deed || []).filter((i) => (i.kind === "doc" || !at.lines.length) && !covered.has(i.key));
    const n = at.lines.length + deedDocs.length;
    if (!n) return null;
    const blocked = (p.card?.blocks || []).length > 0;
    const shownAll = all ? at.lines : at.lines.slice(0, 4);
    return (
      <section className={`bg-white rounded-[18px] border-2 ${blocked ? "border-red-200" : "border-rose-100"} shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden`} data-testid="pending-block">
        <div className="flex flex-wrap items-center gap-2 px-[18px] md:px-6 py-4 border-b border-rose-100">
          <AlertTriangle size={20} className="text-red-600 shrink-0" />
          <h2 className="flex-1 min-w-0 font-heading text-lg md:text-xl navy-text" data-testid="travar-title">O que falta para a escritura <span className="text-slate-500 font-normal text-base">({n} {n === 1 ? "item pendente" : "itens pendentes"})</span></h2>
        </div>
        {at.lines.length > 0 && (
          <div className="divide-y divide-slate-100">
            {shownAll.map((ln) => <Line key={ln.id} ln={ln} run={run} />)}
            {at.lines.length > 4 && <button onClick={() => setAll(!all)} data-testid="pending-more" className="w-full px-6 py-2.5 text-left text-sm font-medium text-ec-green hover:bg-slate-50">{all ? "Ver menos" : `Ver mais ${at.lines.length - 4}`}</button>}
          </div>
        )}
        {deedDocs.length > 0 && <DeedDocs p={p} items={deedDocs} others={at.lines.length > 0} />}
        {dialogs}
      </section>
    );
  }
  if (!at.lines.length && !head) return <>{info}{between}</>;
  if (at.groups?.length) {
    const mine = at.lines.find((ln) => ln.dono === at.groups[0]?.key && at.groups[0]?.label === "Contigo") || null;
    return (
      <div className="space-y-3" data-testid="pending-block">
        <h2 className="sr-only" data-testid="pending-title">A tratar</h2>
        {info}
        {head || (mine ? <NextCard ln={mine} run={run} mineLabel /> : <StatusNote status={status} boxed />)}
        {between}
        <OwnerGroups at={at} first={head ? null : mine} run={run} />
        {reviewing && <MismatchReview p={p} item={reviewing} onClose={() => setReviewing(null)} onDone={onChange} />}
        {asking && <QuestionsDialog p={p} keys={asking.keys} who={asking.who} onClose={() => setAsking(null)} onSaved={onChange} />}
        <DatesDialog p={p} open={dates} onClose={() => setDates(false)} onSaved={() => { setDates(false); onChange(); }} />
      </div>
    );
  }
  const [first, ...rest] = head ? [null, ...at.lines] : at.lines;
  const shown = all ? rest : rest.slice(0, 3);
  return (
    <div className="space-y-3" data-testid="pending-block">
      <h2 className="sr-only" data-testid="pending-title">A tratar</h2>
      {info}
      {head || <NextCard ln={first} run={run} />}
      {between}
      {rest.length > 0 && (
        <div className="bg-white rounded-[18px] shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100 overflow-hidden">
          <div className="px-[18px] md:px-6 pt-4 pb-2 text-xs font-bold tracking-wider text-slate-500 uppercase" data-testid="pending-week-title">Também esta semana · {rest.length}</div>
          <div className="divide-y divide-slate-100 border-t border-slate-100">
            {shown.map((ln) => <Line key={ln.id} ln={ln} run={run} />)}
          </div>
          {rest.length > 3 && (
            <button onClick={() => setAll(!all)} data-testid="pending-more"
              className="w-full border-t border-slate-100 px-6 py-2.5 text-left text-sm font-medium text-ec-green hover:bg-slate-50">
              {all ? "Ver menos" : at.more_text || `Ver mais ${rest.length - 3}`}
            </button>
          )}
        </div>
      )}
      {reviewing && <MismatchReview p={p} item={reviewing} onClose={() => setReviewing(null)} onDone={onChange} />}
      {asking && <QuestionsDialog p={p} keys={asking.keys} who={asking.who} onClose={() => setAsking(null)} onSaved={onChange} />}
      <DatesDialog p={p} open={dates} onClose={() => setDates(false)} onSaved={() => { setDates(false); onChange(); }} />
    </div>
  );
}
